import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { installerProgressStage, installerApi } from '../../utils/installerApi';
import { useInstallerSession } from '../../hooks/useInstallerSession';
import { useInstallerPresence } from '../../hooks/useInstallerPresence';
import { useOfflineQueue } from '../../installer/pwa/useOfflineQueue';
import { InstallerTopbar } from './InstallerChrome';
import { InstallerFollowups } from './InstallerFindings';
import OfferCard from './OfferCard';
import CarrierBadge from '../carrier/CarrierBadge';
import InstallerPushToggle from './InstallerPushToggle';
import OfflineBanner from './OfflineBanner';
import InstallBanner from './InstallBanner';
import '../../styles/InstallerPages.css';
import { installerStageState } from '../../shared/installationStages';
import { JOB_KIND_LABELS, jobKindOf, WORK_FAMILIES, workFamilyOf, jobTitle } from '../../shared/installationProcess';
import { toFa } from '../../utils/faFormat';
import { formatEn } from '../../utils/faFormat';

const STATUS = {
  on_hold: {cls:'is-pending',label:'ناتمام؛ در انتظار تصمیم دفتر'},
  assigned: { cls: 'is-assigned', label: 'تخصیص‌داده‌شده' },
  in_progress: { cls: 'is-progress', label: 'در حال انجام' },
};
const OFFERS_POLL_MS = 5000;

export default function InstallerDashboardPage() {
  const nav = useNavigate();
  const { token } = useInstallerSession();
  const { online, goOnline, goOffline, geoError } = useInstallerPresence(token);
  const [jobs, setJobs] = useState(null);
  const [offers, setOffers] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = useCallback(() => {
    if (!token) return;
    installerApi.jobs(token).then((r) => setJobs(r.jobs)).catch((e) => setErr(e.message));
  }, [token]);

  useEffect(() => {
    if (!token) { nav('/installer/login'); return; }
    loadJobs();
  }, [token, nav, loadJobs]);

  const { queuedIds, drafts = [], notice } = useOfflineQueue(token, loadJobs);

  const loadOffers = useCallback(() => {
    if (!token) return;
    installerApi.offers(token).then((r) => setOffers(r.offers || [])).catch(() => {});
  }, [token]);

  // Poll offers regardless of online: with push, a near-but-offline installer can
  // be OFFERED a job and is alerted to open the app, so the offer must be visible
  // (and acceptable) even before they tap «آماده به کار».
  useEffect(() => {
    if (!token) return undefined;
    loadOffers();
    pollRef.current = setInterval(loadOffers, OFFERS_POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [token, loadOffers]);

  const accept = async (offer) => {
    setBusy(true); setErr(null);
    try {
      const r = await installerApi.acceptOffer(token, offer.id);
      if (r.ok) { nav(`/installer/job/${r.job_id}`); return; }
      if (r.status === 409) setErr('این کار قبلا گرفته شد.');
      else if (r.status === 410) setErr('مهلت این پیشنهاد تمام شد.');
      else setErr(r.error || 'پذیرش ناموفق بود.');
      loadOffers();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const decline = async (offer) => {
    setBusy(true);
    try { await installerApi.declineOffer(token, offer.id); setOffers((xs) => xs.filter((o) => o.id !== offer.id)); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const needsAction = (j) => drafts.some((d) => d.jobId === j.id) || ['open', 'failed'].includes(j.progress?.current_state);
  const orderedJobs = jobs && [...jobs].sort((a, b) => Number(needsAction(b)) - Number(needsAction(a)));
  const hasGroups = jobs?.some(needsAction) && jobs?.some((j) => !needsAction(j));
  return (
    <div className="inst">
      <div className="inst-shell">
        <InstallerTopbar sub="کارهای من" action={(
          <Link to="/installer/profile" className="inst-icon-btn" aria-label="پروفایل و تنظیمات">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
            </svg>
          </Link>
        )} />
        <OfflineBanner />
        <InstallBanner />

        <div className="inst-card inst-availability">
          <div>
            <div className="inst-avail-title">وضعیت کاری</div>
            <div className={`inst-avail-state ${online ? 'is-on' : ''}`}>{online ? 'آنلاین - آماده دریافت کار' : 'آفلاین'}</div>
            {geoError && online && <div className="inst-avail-hint">دسترسی به موقعیت داده نشد؛ از آدرس پایه استفاده می‌شود.</div>}
          </div>
          <button className={online ? 'inst-btn inst-btn-ghost' : 'inst-btn'} onClick={() => (online ? goOffline() : goOnline())}>
            {online ? 'پایان آمادگی' : 'آماده به کار'}
          </button>
        </div>

        <InstallerPushToggle token={token} />
        <InstallerFollowups key={token} token={token} />

        {notice && <p className="inst-alert" role="status">{notice}</p>}
        {err && <div role="alert" className="inst-alert">{err}</div>}

        {offers.map((o) => (
          <OfferCard key={o.id} offer={o} onAccept={accept} onDecline={decline} busy={busy} />
        ))}

        {jobs && jobs.length === 0 && (
          <div className="inst-card">
            <div className="inst-empty">
              <div className="inst-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" /></svg>
              </div>
              <div className="inst-empty-title">کاری به شما تخصیص داده نشده است.</div>
              <div className="inst-empty-hint">برای دریافت کار، وضعیت «آماده به کار» را روشن کنید.</div>
            </div>
          </div>
        )}

        <div className="inst-jobs">
          {hasGroups && <h2 className="inst-h1">نیاز به اقدام شما</h2>}
          {orderedJobs && orderedJobs.map((j, idx) => {
            const stageState = j.progress && installerStageState(installerProgressStage(j.progress));
            const st = j.status === 'on_hold' ? STATUS.on_hold : stageState ? { cls: stageState.tone === 'failed' ? 'is-pending' : stageState.tone === 'ready' ? 'is-assigned' : 'is-progress', label: stageState.label } : STATUS[j.status] || { cls: 'is-pending', label: formatEn(j.status) };
            return (
              <Link key={j.id} to={`/installer/job/${j.id}`} className="inst-jobcard" style={{ animationDelay: `${idx * 50}ms` }}>
                <div className="inst-jobcard-top">
                  <span className="inst-jobcard-name">{jobTitle(j)}</span>
                  <span className={`inst-status ${st.cls}`}>
                    {j.progress && `مرحلهٔ ${toFa(j.progress.current_ord)} از ${toFa(j.progress.total)} · `}{queuedIds.has(j.id) ? 'در صف ارسال' : st.label}
                  </span>
                </div>
                <div className="inst-family-row"><CarrierBadge carrier={j.carrier} variant="inst" /><span className="inst-family">{WORK_FAMILIES[workFamilyOf(j)]}</span><span className="inst-family">{JOB_KIND_LABELS[jobKindOf(j)]}</span></div>
                <div className="inst-jobcard-addr">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                  <span>{j.customer_address_text}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
